/**
 * Single source of truth for entity field length / format bounds shared
 * between backend `class-validator` decorators and frontend zod schemas.
 *
 * Without these constants, the backend DTO's `@MinLength(N) @MaxLength(M)`
 * and the frontend form's `z.string().min(N).max(M)` are duplicated
 * literals — a silent drift in one side produces 400-on-submit (frontend
 * accepts what backend rejects) or a dead-button (frontend rejects with
 * silent regex). Centralizing here makes drift fail at type-check time.
 *
 * Per Rule 15 / DRY: extract when 3+ pairs (backend DTO + frontend zod)
 * reference the same bound. Trailer is the trigger consumer (PR #220);
 * driver phone normalization (PR #218) is the other immediate consumer.
 *
 * Adding a new bound: cite the backend decorator file + line in the JSDoc
 * so the source-of-truth chain stays visible.
 *
 * @module @axon/common/constants/entity-validation
 */

// ============================================================================
// Trailer (CreateTrailerDto)
// ============================================================================

/**
 * Trailer business identifier ("trailer number" — fleet-internal label).
 * Backend: `backend-tms/src/trailers/dto/create-trailer.dto.ts`
 * `@IsString() @MinLength(3) @MaxLength(20)`.
 */
export const TRAILER_NUMBER_MIN_LENGTH = 3;
export const TRAILER_NUMBER_MAX_LENGTH = 20;

/**
 * License plate. Backend `@IsString() @MinLength(3) @MaxLength(20)`.
 */
export const LICENSE_PLATE_MIN_LENGTH = 3;
export const LICENSE_PLATE_MAX_LENGTH = 20;

/**
 * US state code (always 2 chars uppercase, e.g. `TX`, `CA`).
 * Backend `@IsString() @MinLength(2) @MaxLength(2)` (optional via
 * `@IsOptional()`).
 */
export const US_STATE_CODE_LENGTH = 2;

/**
 * VIN — Vehicle Identification Number. Always exactly 17 chars per
 * ISO 3779. Backend `@IsString() @MinLength(17) @MaxLength(17)` (optional).
 *
 * Spec excludes letters `I`, `O`, `Q` to avoid confusion with `1` / `0`,
 * but enforcement of that subset is downstream (separate ticket).
 */
export const VIN_LENGTH = 17;

// ============================================================================
// Phone (Driver.mobileNumber, CustomerContact.phone, etc.)
// ============================================================================

/**
 * Digits-only phone bounds — paired with `stripNonDigits` preprocess in
 * form schemas (`@axon/common/utils/normalize-phone`). Backend decorator:
 * `@Matches(/^[0-9]{10,20}$/)`.
 *
 * Lower 10 = shortest carrier-issued mobile (US without country code,
 * TR without `90` prefix). Upper 20 = generous for international
 * country-code + extension formats.
 */
export const PHONE_DIGITS_MIN_LENGTH = 10;
export const PHONE_DIGITS_MAX_LENGTH = 20;
